import { sectionRenderer } from '@theme/section-renderer';
import { ThemeEvents } from '@theme/events';

const QUERY_PARAMETER = 'collection_q';
const DEBOUNCE_DELAY = 300;

class CollectionSearch extends HTMLElement {
  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {number | undefined} */
  #debounceTimer;

  /** @type {MutationObserver | null} */
  #filterUpdateObserver = null;

  /** @type {number | undefined} */
  #filterUpdateTimeout;

  connectedCallback() {
    this.input = this.querySelector('[data-collection-search-input]');
    if (!(this.input instanceof HTMLInputElement)) return;

    const query = new URL(window.location.href).searchParams.get(QUERY_PARAMETER) ?? '';
    this.input.value = query;
    this.#preserveQueryInFacetForms(query);

    this.input.addEventListener('input', this.#handleInput);
    this.input.addEventListener('keydown', this.#handleKeydown);
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);

    if (query && this === document.querySelector('collection-search')) {
      this.#search(query);
    }
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#filterUpdateObserver?.disconnect();
    clearTimeout(this.#debounceTimer);
    clearTimeout(this.#filterUpdateTimeout);
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  #handleInput = () => {
    clearTimeout(this.#debounceTimer);
    this.#syncInputs(this.input.value);
    this.#debounceTimer = window.setTimeout(() => this.#search(this.input.value), DEBOUNCE_DELAY);
  };

  #handleKeydown = (event) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    clearTimeout(this.#debounceTimer);
    this.#search(this.input.value);
  };

  #handleFilterUpdate = () => {
    const query = new URL(window.location.href).searchParams.get(QUERY_PARAMETER)?.trim() ?? '';
    if (!query) return;

    const section = document.getElementById(`shopify-section-${this.sectionId.replace(/^shopify-section-/, '')}`);
    if (!section) return;

    this.#filterUpdateObserver?.disconnect();
    clearTimeout(this.#filterUpdateTimeout);

    this.#filterUpdateObserver = new MutationObserver(() => {
      this.#filterUpdateObserver?.disconnect();
      clearTimeout(this.#filterUpdateTimeout);

      if (!this.isConnected) return;

      const input = this.querySelector('[data-collection-search-input]');
      if (!(input instanceof HTMLInputElement)) return;

      this.input = input;
      this.input.value = query;
      this.#preserveQueryInFacetForms(query);
      this.#search(query);
    });

    this.#filterUpdateObserver.observe(section, { childList: true, subtree: true });
    this.#filterUpdateTimeout = window.setTimeout(() => this.#filterUpdateObserver?.disconnect(), 5000);
  };

  async #search(rawQuery) {
    const query = rawQuery.trim();
    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    if (query) {
      url.searchParams.set(QUERY_PARAMETER, query);
    } else {
      url.searchParams.delete(QUERY_PARAMETER);
    }

    history.replaceState({}, '', url);
    this.#preserveQueryInFacetForms(query);
    this.#syncInputs(query);

    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    if (!query) {
      await sectionRenderer.renderSection(this.sectionId, { cache: false, url });
      return;
    }

    this.#setLoading(true);

    try {
      const firstDocument = await this.#fetchPage(url, 1, signal);
      const firstGrid = firstDocument.querySelector('[ref="grid"]');
      const pageCount = Number(firstGrid?.dataset.lastPage ?? 1);
      const documents = [firstDocument];

      for (let start = 2; start <= pageCount; start += 4) {
        const requests = [];
        for (let page = start; page < Math.min(start + 4, pageCount + 1); page += 1) {
          requests.push(this.#fetchPage(url, page, signal));
        }
        documents.push(...(await Promise.all(requests)));
      }

      if (signal.aborted) return;

      const normalizedQuery = this.#normalize(query);
      const matches = documents.flatMap((document) =>
        Array.from(document.querySelectorAll('[data-collection-search-text]')).filter((card) =>
          this.#normalize(card.dataset.collectionSearchText ?? '').includes(normalizedQuery)
        )
      );

      this.#renderMatches(matches);
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Collection search failed', error);
      }
    } finally {
      if (!signal.aborted) this.#setLoading(false);
    }
  }

  async #fetchPage(baseUrl, page, signal) {
    const url = new URL(baseUrl);
    url.searchParams.set('page', String(page));
    url.searchParams.set('section_id', this.sectionId.replace(/^shopify-section-/, ''));

    const response = await fetch(url, { signal });
    if (!response.ok) throw new Error(`Collection page request failed: ${response.status}`);

    return new DOMParser().parseFromString(await response.text(), 'text/html');
  }

  #renderMatches(matches) {
    const section = document.getElementById(`shopify-section-${this.sectionId.replace(/^shopify-section-/, '')}`);
    const grid = section?.querySelector('[ref="grid"]');
    if (!(grid instanceof HTMLElement)) return;

    section.querySelectorAll('[ref="viewMorePrevious"], [ref="viewMoreNext"], .pagination').forEach((element) => element.remove());
    grid.replaceChildren(...matches.map((card) => card.cloneNode(true)));
    grid.dataset.lastPage = '1';

    let emptyState = section.querySelector('[data-collection-search-empty]');
    if (matches.length > 0) {
      emptyState?.remove();
      return;
    }

    if (!emptyState) {
      emptyState = document.createElement('div');
      emptyState.className = 'main-collection-grid__empty';
      emptyState.dataset.collectionSearchEmpty = '';
      emptyState.innerHTML = '<h2 class="main-collection-grid__empty-title h2">Nessun prodotto trovato</h2><p>Prova con un altro termine.</p>';
      grid.after(emptyState);
    }
  }

  #preserveQueryInFacetForms(query) {
    document.querySelectorAll('facets-form-component form').forEach((form) => {
      let input = form.querySelector(`input[name="${QUERY_PARAMETER}"]`);
      if (!query) {
        input?.remove();
        return;
      }
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = QUERY_PARAMETER;
        form.append(input);
      }
      input.value = query;
    });
  }

  #syncInputs(query) {
    document.querySelectorAll('[data-collection-search-input]').forEach((input) => {
      if (input !== this.input) input.value = query;
    });
  }

  #setLoading(isLoading) {
    const section = document.getElementById(`shopify-section-${this.sectionId.replace(/^shopify-section-/, '')}`);
    section?.querySelector('results-list')?.classList.toggle('loading', isLoading);
    document.querySelectorAll('collection-search').forEach((search) => search.toggleAttribute('aria-busy', isLoading));
  }

  #normalize(value) {
    return value
      .toLocaleLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim();
  }

  get sectionId() {
    return this.getAttribute('section-id') ?? '';
  }
}

if (!customElements.get('collection-search')) {
  customElements.define('collection-search', CollectionSearch);
}
